import { useEffect, useMemo, useRef } from 'react';

/**
 * Returns a debounced version of the given function.
 *
 * A debounced function is delayed until it hasn't been called for a given interval.
 *
 * @param callback  the function to be debounced
 * @param wait      debounce interval in ms
 * @returns         the debounced function
 */
export const debounce = <T extends (...args: any) => void>(
  callback: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: number | undefined;
  return function (...args: Parameters<T>[]): void {
    if (timeout) {
      window.clearTimeout(timeout);
    }
    timeout = window.setTimeout(function (): void {
      timeout = undefined;
      callback(...args);
    }, wait);
  };
};

/**
 * Hook to easier debounce functions in functional components.
 * Debouncing is done once at the beginning, but still always the latest given callback is called.
 */
export const useDebounce = <T extends (...args: any) => void>(
  callback: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedCallback = useMemo(
    () => debounce((...args: Parameters<T>[]) => callbackRef.current(...args), wait),
    [wait]
  );

  return debouncedCallback;
};
